import { DuplicateWorkflowModalElement } from './DuplicateWorkflowModal'

type ContentElementDetails = {
  ownSubjectLine?: string
  elementType?: string
  optimisationType?: string
  displayName?: string
  projectId?: string
}

type DuplicatePreparationResult = {
  isPersonaliseWorkflow: boolean
  allowHumanControlEdit: boolean
  canIncludeGeneratedContent: boolean
  templatesWithoutGeneratedContent: string[]
  elementDetailsById: Map<number, ContentElementDetails>
}

const isPersonalizeElement = (element: any) =>
  element?.personalization_settings?.type === 'personalize'

const isOptimizeElement = (element: any) =>
  element?.optimization_settings?.type === 'optimize'

const isContentOrOptimizeElement = (element: any) =>
  element?.optimization_settings?.type === 'content' ||
  isOptimizeElement(element)

const hasCloneableGeneratedVariant = (element: any) =>
  element?.variants?.some(
    (variant: any) => variant?.status === 'active' && !variant?.ownsl
  )

const hasExplicitOptimizationType = (element: any) =>
  Boolean(element?.optimization_settings?.type)

const isEligibleForGeneratedDuplication = (element: any) => {
  if (isPersonalizeElement(element)) {
    return false
  }

  // If the element is not an optimize element but is a content element.
  if (!hasExplicitOptimizationType(element)) {
    return hasCloneableGeneratedVariant(element)
  }

  return isContentOrOptimizeElement(element)
}

export const getDuplicatePreparation = (
  content: any
): DuplicatePreparationResult => {
  const elements = content?.elements ?? []
  const isPersonaliseWorkflow = Boolean(
    elements.some((element: any) => isPersonalizeElement(element))
  )
  const isOptimizeWorkflow = Boolean(
    elements.some((element: any) => isOptimizeElement(element))
  )
  const canIncludeGeneratedContent = Boolean(
    elements.some(
      (element: any) =>
        isEligibleForGeneratedDuplication(element) &&
        hasCloneableGeneratedVariant(element)
    )
  )

  const templatesWithoutGeneratedContent = elements.reduce(
    (accumulator, element: any) => {
      if (!isEligibleForGeneratedDuplication(element)) {
        return accumulator
      }

      if (hasCloneableGeneratedVariant(element)) {
        return accumulator
      }

      const templateName = element?.display_name?.trim()
      if (templateName) {
        accumulator.push(templateName)
      }

      return accumulator
    },
    []
  )

  const elementDetailsById = new Map<number, ContentElementDetails>()
  elements.forEach((element: any) => {
    const elementId = element.element_id
    const ownSubjectLine =
      element?.optimization_settings?.configuration?.own_subject_line ??
      element?.personalization_settings?.configuration?.own_subject_line ??
      ''
    const projectId =
      element?.optimization_settings?.configuration?.project_id ??
      element?.personalization_settings?.configuration?.project_id

    if (typeof elementId === 'number') {
      elementDetailsById.set(elementId, {
        ownSubjectLine,
        elementType: element.element_type,
        optimisationType:
          element?.optimization_settings?.type ??
          element?.personalization_settings?.type,
        displayName: element?.display_name ?? element?.name,
        projectId,
      })
    }
  })

  return {
    isPersonaliseWorkflow,
    allowHumanControlEdit: isPersonaliseWorkflow || isOptimizeWorkflow,
    canIncludeGeneratedContent,
    templatesWithoutGeneratedContent,
    elementDetailsById,
  }
}

export const mergeElementDetailsIntoDuplicateModalElements = (
  elements: DuplicateWorkflowModalElement[],
  elementDetailsById: Map<number, ContentElementDetails>
): DuplicateWorkflowModalElement[] =>
  elements.map((element) => {
    const details = elementDetailsById.get(element.elementId)
    return {
      ...element,
      ownSubjectLine: details?.ownSubjectLine,
      elementType: details?.elementType,
      optimisationType: details?.optimisationType,
      displayName: details?.displayName,
      projectId: details?.projectId ?? element.projectId,
    }
  })

export const getElementModeLabel = (
  element: DuplicateWorkflowModalElement
): 'OPTIMISE' | 'PERSONALISE' | null => {
  if (
    element.optimisationType === 'optimize' ||
    element.optimisationType === 'optimise'
  ) {
    return 'OPTIMISE'
  }
  if (
    element.optimisationType === 'personalize' ||
    element.optimisationType === 'personalise'
  ) {
    return 'PERSONALISE'
  }
  return null
}

export const getMultipleTemplatesBannerText = (
  controllableElements: DuplicateWorkflowModalElement[]
): string => {
  const hasOptimiseTemplates = controllableElements.some(
    (e) =>
      e.optimisationType === 'optimize' || e.optimisationType === 'optimise'
  )
  const hasPersonalizeTemplates = controllableElements.some(
    (e) =>
      e.optimisationType === 'personalize' ||
      e.optimisationType === 'personalise'
  )

  if (hasOptimiseTemplates && hasPersonalizeTemplates) {
    return 'This workflow contains optimisation and personnalised templates. Update its settings before duplicating.'
  }

  if (hasPersonalizeTemplates) {
    return 'This workflow contains personalization templates. Update its settings before duplicating.'
  }

  // Default to optimisation templates if we cannot infer a personalize/optimise mix.
  return 'This workflow contains optimisation templates. Update its settings before duplicating.'
}
